import SocialCampaignToken from '@apps/campaigns/admin/tokens/campaign/social'
import Infinite from '@admin/components/infinite'
import PropTypes from 'prop-types'
import moment from 'moment'
import React from 'react'

class Grid extends React.PureComponent {

  static contextTypes = {
    alert: PropTypes.object,
    router: PropTypes.object
  }

  static propTypes = {
    endpoint: PropTypes.string,
    onSchedule: PropTypes.func
  }

  state = {
    hover: false,
    columns: 3
  }

  gridRef = React.createRef()

  _handleDragEnter = this._handleDragEnter.bind(this)
  _handleDragLeave = this._handleDragLeave.bind(this)
  _handleDragOver = this._handleDragOver.bind(this)
  _handleDrop = this._handleDrop.bind(this)
  _handleItemDrop = this._handleItemDrop.bind(this)

  componentDidMount() {
    this._setupResizeObserver()
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  render() {
    return (
      <div className="campaigns-social-schedule-grid" ref={this.gridRef}>
        <Infinite { ...this._getInfinite() } />
      </div>
    )
  }

  _setupResizeObserver() {
    if (this.gridRef.current && window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const width = entry.contentRect.width
          let columns = 3
          
          if (width < 400) {
            columns = 1
          } else if (width < 800) {
            columns = 2
          }
          
          if (columns !== this.state.columns) {
            this.setState({ columns })
          }
        }
      })
      
      this.resizeObserver.observe(this.gridRef.current)
    }
  }

  _getInfinite() {
    const { endpoint } = this.props
    return {
      endpoint,
      filter: { 
        status: { $neq: 'draft' } 
      },
      sort: {
        post_at: 'desc'
      },
      empty: {
        svg: 'messages_square',
        title: 't(No Posts)',
        text: 't(There are no posts to display)'
      },
      entity: 'social campaign',
      layout: ({ records }) => {
        const scheduled = records.filter(c => c.status === 'scheduled')
        const posted = records.filter(c => c.status === 'posted')
        
        return (
          <div className="campaigns-social-schedule-grid-sections">
            <div className="campaigns-social-schedule-grid-section">
              <div className="campaigns-social-schedule-grid-header">
                <span>Scheduled</span>
                <span className="count">{ scheduled.length }</span>
              </div>
              <div { ...this._getScheduledSection() }>
                { scheduled.length > 0 ? (
                  scheduled.map((social_campaign, index) => (
                    <div { ...this._getGridItem(social_campaign, true) } key={`scheduled_${social_campaign.id}`}>
                      <div className="campaigns-social-schedule-grid-item-content">
                        <SocialCampaignToken social_campaign={ social_campaign } mode="grid" />
                      </div>
                      <div className="campaigns-social-schedule-grid-item-time">
                        { moment(social_campaign.post_at).format('MMM D, h:mm A') }
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="campaigns-social-schedule-grid-empty">
                    Drop campaigns here to schedule
                  </div>
                ) }
              </div>
            </div>
            
            { posted.length > 0 && (
              <div className="campaigns-social-schedule-grid-section">
                <div className="campaigns-social-schedule-grid-header">
                  <span>Posted</span>
                  <span className="count">{ posted.length }</span>
                </div>
                <div className={`campaigns-social-schedule-grid-items posted columns-${this.state.columns}`}>
                  { posted.map((social_campaign, index) => (
                    <div { ...this._getGridItem(social_campaign, false) } key={`posted_${social_campaign.id}`}>
                      <div className="campaigns-social-schedule-grid-item-content">
                        <SocialCampaignToken social_campaign={ social_campaign } mode="grid" />
                      </div>
                      <div className="campaigns-social-schedule-grid-item-time">
                        { moment(social_campaign.post_at).format('MMM D, h:mm A') }
                      </div>
                    </div>
                  )) }
                </div>
              </div>
            )}
          </div>
        )
      }
    }
  }

  _getScheduledSection() {
    const { hover, columns } = this.state
    const classes = ['campaigns-social-schedule-grid-items', 'scheduled', `columns-${columns}`]
    if(hover) classes.push('hover')
    return {
      className: classes.join(' '),
      onDragEnter: this._handleDragEnter,
      onDragLeave: this._handleDragLeave,
      onDragOver: this._handleDragOver,
      onDrop: this._handleDrop
    }
  }

  _getGridItem(social_campaign, isDraggable) {
    return {
      className: 'campaigns-social-schedule-grid-item',
      title: social_campaign.title,
      'data-status': social_campaign.status,
      'data-campaign-id': social_campaign.id,
      'data-post-at': social_campaign.post_at,
      draggable: isDraggable,
      onClick: this._handleClick.bind(this, social_campaign),
      onDragStart: isDraggable ? this._handleDragStart.bind(this, social_campaign) : undefined,
      onDrop: this._handleItemDrop.bind(this, social_campaign)
    }
  }

  _handleClick(social_campaign) {
    this.context.router.push(`/admin/campaigns/social/${social_campaign.id}`)
  }

  _handleDragStart(social_campaign, e) {
    e.stopPropagation()
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('social_campaign', JSON.stringify(social_campaign))
  }

  _handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ hover: true })
  }

  _handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
  }

  _handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    // Only clear hover if leaving the container completely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      this.setState({ hover: false })
    }
  }

  _handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ hover: false })
    const data = e.dataTransfer.getData('social_campaign')
    if(!data) return
    const social_campaign = JSON.parse(data)
    if(social_campaign?.status === 'draft') {
      this.props.onSchedule(social_campaign)
    }
  }

  _handleItemDrop(targetCampaign, e) {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ hover: false })
    const data = e.dataTransfer.getData('social_campaign')
    if(!data) return
    const social_campaign = JSON.parse(data)
    if(social_campaign.id === targetCampaign.id) return
    if(targetCampaign.status === 'scheduled' && (social_campaign.status === 'draft' || social_campaign.status === 'scheduled')) {
      const post_at = moment(targetCampaign.post_at).clone().add(1, 'hour').format('YYYY-MM-DD HH:mm')
      this.props.onSchedule(social_campaign, post_at)
    }
  }

}

export default Grid